import { useState, useEffect, useRef, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Custom hook for real-time collaborative note editing via WebSocket
 * @param {string} noteId - The note ID to connect to
 * @param {boolean} active - Whether the connection should be active
 * @returns {object} - { content, title, editors, typingUsers, isConnected, sendUpdate, sendCursor, sendTyping }
 */
const useCollabEditor = (noteId, active = false) => {
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [editors, setEditors] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const typingTimeoutRef = useRef({});

  const getWsUrl = useCallback(() => {
    if (!noteId) return null;
    const base = BACKEND_URL.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
    // httpOnly cookies are sent automatically during WS handshake
    return `${base}/api/ws/notes/${noteId}`;
  }, [noteId]);

  const connect = useCallback(() => {
    const url = getWsUrl();
    if (!url || !active) return;

    // Close existing
    if (wsRef.current) {
      wsRef.current.close();
    }

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'init':
            setContent(data.note?.content || '');
            setTitle(data.note?.title || '');
            setEditors(data.editors || []);
            break;

          case 'content_update':
            setContent(data.content || '');
            if (data.title) setTitle(data.title);
            break;

          case 'user_joined':
          case 'user_left':
            setEditors(data.editors || []);
            break;

          case 'typing_status': {
            const userId = data.user_id;
            if (data.typing) {
              setTypingUsers(prev => {
                if (prev.some(u => u.user_id === userId)) return prev;
                return [...prev, { user_id: userId, user_name: data.user_name }];
              });
              // Auto-clear typing after 3s
              if (typingTimeoutRef.current[userId]) {
                clearTimeout(typingTimeoutRef.current[userId]);
              }
              typingTimeoutRef.current[userId] = setTimeout(() => {
                setTypingUsers(prev => prev.filter(u => u.user_id !== userId));
              }, 3000);
            } else {
              setTypingUsers(prev => prev.filter(u => u.user_id !== userId));
            }
            break;
          }

          case 'cursor_update':
            setEditors(prev =>
              prev.map(e =>
                e.user_id === data.user_id ? { ...e, cursor: data.cursor } : e
              )
            );
            break;

          case 'pong':
            break;

          default:
            break;
        }
      } catch (e) {
        console.error('WS parse error:', e);
      }
    };

    ws.onclose = (event) => {
      setIsConnected(false);
      wsRef.current = null;

      // Auto-reconnect unless intentionally closed
      if (active && event.code !== 1000 && event.code !== 4001 && event.code !== 4003) {
        reconnectTimerRef.current = setTimeout(() => {
          connect();
        }, 2000);
      }
    };

    ws.onerror = () => {
      setIsConnected(false);
    };
  }, [getWsUrl, active]);

  useEffect(() => {
    if (active && noteId) {
      connect();
    }

    const typingTimeouts = typingTimeoutRef.current;
    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000);
        wsRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      Object.values(typingTimeouts).forEach(clearTimeout);
    };
  }, [active, noteId, connect]);

  const sendUpdate = useCallback((newContent, newTitle) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'content_update',
        content: newContent,
        title: newTitle || title,
      }));
    }
  }, [title]);

  const sendCursor = useCallback((position) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'cursor_update',
        cursor: position,
      }));
    }
  }, []);

  const sendTyping = useCallback((typing) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'typing_status',
        typing,
      }));
    }
  }, []);

  return {
    content,
    setContent,
    title,
    setTitle,
    editors,
    typingUsers,
    isConnected,
    sendUpdate,
    sendCursor,
    sendTyping,
  };
};

export default useCollabEditor;
